import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "384",
  "slug": "highest-capital-cities-in-the-world",
  "title": "The Highest Capital Cities in the World",
  "excerpt": "Some national capitals sit higher than most ski resorts. Here are the highest capital cities on Earth, why people built there, and what life is like when the air is thin.",
  "date": "2026-06-09",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1596118769843-08e9ad381ab0?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxsYSUyMHBheiUyMGJvbGl2aWF8ZW58MXwwfHx8MTc4MDk5MjgyOHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "La Paz, Bolivia is the highest capital city in the world at around 3,640 metres, followed by Quito, Ecuador and Bogotá, Colombia. Most of the highest capitals sit in the Andes or the East African highlands, where elevation makes the tropics livable.",
  "content": [
    {
      "type": "paragraph",
      "text": "We tend to picture capital cities at sea level: ports, river crossings, sprawling lowland metropolises. But a handful of the world’s capitals sit so high that visitors arrive short of breath. These high-altitude capitals cluster in just a few mountain regions, and the reasons people built cities up there are as much about climate and history as about geography."
    },
    {
      "type": "heading",
      "text": "The Highest Capitals on Earth"
    },
    {
      "type": "paragraph",
      "text": "Ranking capitals by elevation, the top of the list is dominated by the Andes:"
    },
    {
      "type": "list",
      "items": [
        "La Paz, Bolivia — about 3,640 metres, the highest administrative capital in the world. The airport at nearby El Alto sits even higher.",
        "Quito, Ecuador — around 2,850 metres, perched in a high Andean valley almost on the equator.",
        "Sucre, Bolivia — roughly 2,810 metres, Bolivia’s constitutional capital.",
        "Bogotá, Colombia — about 2,640 metres, one of the highest major cities in the world.",
        "Addis Ababa, Ethiopia — around 2,355 metres, the highest capital in Africa.",
        "Thimphu, Bhutan, and Asmara, Eritrea — both sitting well above 2,000 metres."
      ]
    },
    {
      "type": "paragraph",
      "text": "Bolivia is the only country that appears twice, thanks to its split arrangement: La Paz is the seat of government while Sucre is the constitutional capital. That quirk is worth remembering the next time a quiz asks for Bolivia’s capital."
    },
    {
      "type": "heading",
      "text": "Why Build a Capital So High?"
    },
    {
      "type": "paragraph",
      "text": "At first glance, high altitude seems like a strange place to put a city. But in the tropics, elevation is a gift. Near the equator, low-lying areas can be hot, humid, and historically disease-prone. Climb a couple of thousand metres and you reach a band locals sometimes call the \"land of eternal spring,\" with mild temperatures all year. That is exactly why Quito, Bogotá, and Addis Ababa grew where they did."
    },
    {
      "type": "paragraph",
      "text": "History matters too. Many Andean cities were founded near pre-Columbian population centres, where the Inca and earlier cultures had already farmed terraced highland valleys for centuries. Spanish colonizers often built directly on top of those existing hubs."
    },
    {
      "type": "heading",
      "text": "What Thin Air Does to a City"
    },
    {
      "type": "paragraph",
      "text": "At 3,600 metres, the air holds roughly a third less oxygen than at sea level. Visitors to La Paz frequently feel altitude sickness: headaches, fatigue, and breathlessness on the smallest hill. Locals are physiologically adapted, with differences in lung capacity and blood that develop over generations."
    },
    {
      "type": "paragraph",
      "text": "Altitude shapes daily life in subtler ways too. Water boils at a lower temperature, so cooking takes longer. Sports teams from lowland countries dread playing matches in these cities. And engines, aircraft, and even kettles all behave a little differently in the thin mountain air."
    },
    {
      "type": "heading",
      "text": "Spotting High Cities From Above"
    },
    {
      "type": "paragraph",
      "text": "High-altitude capitals look distinctive from satellite view. They sit in or beside dramatic terrain: La Paz famously spills down the sides of a canyon, with the flat sprawl of El Alto on the rim above. Quito stretches in a long, narrow ribbon squeezed between Andean ridges. The surrounding landscape, brown peaks, terraced slopes, snow lines, often gives the location away before you ever read a single sign."
    },
    {
      "type": "heading",
      "text": "A Geography Lesson in Elevation"
    },
    {
      "type": "paragraph",
      "text": "The highest capitals teach a tidy lesson: in geography, latitude and altitude trade off against each other. Climbing a mountain has a similar effect on temperature as traveling toward the poles. That is why you can find cool, spring-like cities sitting almost on the equator, and why elevation is one of the most important things to notice when you are trying to read a landscape."
    },
    {
      "type": "heading",
      "text": "Altitude and the World of Sport"
    },
    {
      "type": "paragraph",
      "text": "High-altitude capitals even reshape sport. Football teams from sea-level countries often struggle when they travel to play in cities like La Paz or Quito, where the thin air leaves visitors gasping while the home side is fully adapted. The advantage is real enough that it has sparked complaints and heated debate in international competition over the years. Endurance athletes, meanwhile, deliberately train at altitude to push their bodies to carry oxygen more efficiently, which is why high mountain towns attract runners and cyclists from around the world."
    },
    {
      "type": "paragraph",
      "text": "It all comes back to a simple physical fact: lower air pressure means less oxygen in every breath. The human body can adjust, but only slowly, and the higher you go, the more pronounced the effects become. A capital city perched above 3,000 metres is, in a very real sense, asking its residents and visitors to live partway up a mountain."
    }
  ]
};
